import { supabase } from '../utils/supabase';

export interface SessionData {
  id: string;
  session_name: string;
  store_id: string;
  movement_type: 'purchase_in' | 'adjustment' | 'delivery_out' | 'delivery_return' | 'opening_stock';
  created_by: string;
  is_active: boolean;
  remarks?: string;
  delivery_hash?: string;
  total_items: number;
  total_amount: number;
  created_at: string;
  updated_at: string;
}

export interface SessionItem {
  id: string;
  item_id: string;
  quantity: number;
  unit_price: number;
  unit_cost: number;
  total_amount: number;
  order_references?: string;
  item: any;
  current_stock: number;
  average_cost: number;
  total_value: number;
}

export interface SessionResponse {
  success: boolean;
  data?: {
    session: SessionData;
    items: SessionItem[];
  };
  error?: string;
}

export interface SessionItemsResponse {
  success: boolean;
  data?: {
    items: SessionItem[];
  };
  error?: string;
}

export class SessionService {
  /**
   * Get or create a flexible session that supports movement type switching
   */
  static async getOrCreateSession(
    userId: string,
    storeId: string,
    movementType: string,
    allowTypeSwitch: boolean = true
  ): Promise<SessionResponse> {
    try {
      console.log('SessionService: Calling flexible RPC with params:', {
        p_user_id: userId,
        p_store_id: storeId,
        p_movement_type: movementType,
        p_allow_type_switch: allowTypeSwitch
      });

      // Use the new flexible session management RPC
      const { data, error } = await supabase.rpc('get_or_create_flexible_session', {
        p_user_id: userId,
        p_store_id: storeId,
        p_movement_type: movementType,
        p_allow_type_switch: allowTypeSwitch
      });

      if (error) {
        console.error('Session service RPC error:', error);
        return { success: false, error: error.message };
      }

      console.log('SessionService: RPC response:', data);
      
      // Debug the data structure
      console.log('SessionService: Items in response:', data?.data?.items?.length || 0);
      console.log('SessionService: Full data structure:', JSON.stringify(data, null, 2));
      
      // Debug condition evaluation
      console.log('🔍 Fallback condition check:', {
        success: data?.success,
        total_items: data?.data?.session?.total_items,
        items_exists: !!data?.data?.items,
        items_length: data?.data?.items?.length
      });
      
      // If items are missing but session says it has items, use load_session_items RPC
      // This handles timing/transaction issues with the main RPC function
      if (data?.success && data?.data?.session?.total_items > 0 && (!data?.data?.items || data?.data?.items?.length === 0)) {
        console.log('🔄 Main RPC returned empty items, using load_session_items RPC as fallback...');
        
        // Use the load_session_items RPC which we know works properly
        console.log('🔧 About to call load_session_items with session_id:', data.data.session.id);
        const { data: loadResult, error: loadError } = await supabase.rpc('load_session_items', {
          p_session_id: data.data.session.id
        });
        
        if (loadError) {
          console.error('❌ Error loading session items via RPC:', loadError);
          return { success: false, error: loadError.message };
        }
        
        console.log('📦 load_session_items RPC result:', JSON.stringify(loadResult, null, 2));
        
        if (loadResult?.success && loadResult?.data?.items && loadResult.data.items.length > 0) {
          console.log('✅ Successfully loaded items via load_session_items RPC:', loadResult.data.items.length);
          
          // Inject the loaded items into our response
          data.data.items = loadResult.data.items;
          console.log('✅ Items injected into main response');
        } else {
          console.warn('⚠️ load_session_items RPC failed or returned no items, trying enhanced RPC approach...');
          
          // Try the new enhanced RPC function for session items with details
          console.log('🔧 Attempting get_session_items_with_details RPC...');
          
          const { data: sessionItemsResult, error: sessionItemsError } = await supabase.rpc('get_session_items_with_details', {
            p_session_id: data.data.session.id
          });
            
          if (!sessionItemsError && sessionItemsResult?.success && sessionItemsResult.data && sessionItemsResult.data.length > 0) {
            console.log('✅ Enhanced RPC session items query successful, items found:', sessionItemsResult.data.length);
            data.data.items = sessionItemsResult.data;
            console.log('✅ Enhanced RPC items with details injected into response');
          } else {
            console.error('❌ Enhanced RPC session items query failed:', sessionItemsError || sessionItemsResult?.error);
            console.log('ℹ️ No session items found, continuing with empty items array');
          }
        }
        
      }
      
      return data;
    } catch (error) {
      console.error('Session service error:', error);
      return { success: false, error: 'Failed to get or create session' };
    }
  }

  /**
   * Save an item to the current session
   */
  static async saveItemToSession(
    userId: string,
    storeId: string,
    movementType: string,
    itemId: string,
    quantity: number,
    unitPrice: number,
    unitCost: number,
    orderReferences?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await supabase.rpc('save_item_to_session', {
        p_user_id: userId,
        p_store_id: storeId,
        p_movement_type: movementType,
        p_item_id: itemId,
        p_quantity: quantity,
        p_unit_price: unitPrice,
        p_unit_cost: unitCost,
        p_order_references: orderReferences
      });

      if (error) {
        console.error('Save item to session error:', error);
        return { success: false, error: error.message };
      }

      return data;
    } catch (error) {
      console.error('Save item to session error:', error);
      return { success: false, error: 'Failed to save item to session' };
    }
  }

  /**
   * Remove an item from the session
   */
  static async removeItemFromSession(
    userId: string,
    storeId: string,
    movementType: string,
    itemId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await supabase.rpc('remove_item_from_session', {
        p_user_id: userId,
        p_store_id: storeId,
        p_movement_type: movementType,
        p_item_id: itemId
      });

      if (error) {
        console.error('Remove item from session error:', error);
        return { success: false, error: error.message };
      }

      return data;
    } catch (error) {
      console.error('Remove item from session error:', error);
      return { success: false, error: 'Failed to remove item from session' };
    }
  }

  /**
   * Clear all items from a session
   */
  static async clearSession(sessionId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await supabase.rpc('clear_session', {
        p_session_id: sessionId
      });

      if (error) {
        console.error('Clear session error:', error);
        return { success: false, error: error.message };
      }

      return data;
    } catch (error) {
      console.error('Clear session error:', error);
      return { success: false, error: 'Failed to clear session' };
    }
  }

  /**
   * Get all active sessions for a store
   */
  static async getStoreSessions(storeId: string): Promise<{
    success: boolean;
    data?: SessionData[];
    error?: string;
  }> {
    try {
      const { data, error } = await supabase.rpc('get_store_sessions', {
        p_store_id: storeId
      });

      if (error) {
        console.error('Get store sessions error:', error);
        return { success: false, error: error.message };
      }

      return data;
    } catch (error) {
      console.error('Get store sessions error:', error);
      return { success: false, error: 'Failed to get store sessions' };
    }
  }

  /**
   * Load session items (for session sharing)
   */
  static async loadSessionItems(sessionId: string): Promise<SessionResponse> {
    try {
      const { data, error } = await supabase.rpc('load_session_items', {
        p_session_id: sessionId
      });

      if (error) {
        console.error('Load session items error:', error);
        return { success: false, error: error.message };
      }

      return data;
    } catch (error) {
      console.error('Load session items error:', error);
      return { success: false, error: 'Failed to load session items' };
    }
  }

  /**
   * Save delivery hash to session
   */
  static async saveDeliveryHashToSession(
    sessionId: string,
    deliveryHash: string | null,
    userId?: string
  ): Promise<{ success: boolean; error?: string; data?: any }> {
    try {
      const { data, error } = await supabase.rpc('save_delivery_hash_to_session', {
        p_session_id: sessionId,
        p_delivery_hash: deliveryHash,
        p_user_id: userId
      });

      if (error) {
        console.error('Error saving delivery hash to session:', error);
        return { success: false, error: error.message };
      }

      if (data && !data.success) {
        console.error('RPC error saving delivery hash:', data.error);
        return { success: false, error: data.error };
      }

      return { success: true, data: data?.data };
    } catch (error: any) {
      console.error('Exception saving delivery hash to session:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Convert session to permanent movement
   */
  static async convertSessionToMovement(
    sessionId: string,
    remarks?: string,
    deliveryHash?: string
  ): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const { data, error } = await supabase.rpc('convert_session_to_movement', {
        p_session_id: sessionId,
        p_remarks: remarks,
        p_delivery_hash: deliveryHash || null
      });

      if (error) {
        console.error('Convert session to movement error:', error);
        return { success: false, error: error.message };
      }

      return data;
    } catch (error) {
      console.error('Convert session to movement error:', error);
      return { success: false, error: 'Failed to convert session to movement' };
    }
  }

  /**
   * Manually create a new session (even if one exists)
   */
  static async createNewSession(
    userId: string,
    storeId: string,
    movementType: string,
    remarks?: string
  ): Promise<SessionResponse> {
    try {
      const { data, error } = await supabase.rpc('create_new_session', {
        p_user_id: userId,
        p_store_id: storeId,
        p_movement_type: movementType,
        p_remarks: remarks
      });

      if (error) {
        console.error('Create new session error:', error);
        return { success: false, error: error.message };
      }

      return data;
    } catch (error) {
      console.error('Create new session error:', error);
      return { success: false, error: 'Failed to create new session' };
    }
  }

  /**
   * Close a session (deactivate without converting to movement)
   */
  static async closeSession(sessionId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await supabase.rpc('close_session', {
        p_session_id: sessionId
      });

      if (error) {
        console.error('Close session error:', error);
        return { success: false, error: error.message };
      }

      return data;
    } catch (error) {
      console.error('Close session error:', error);
      return { success: false, error: 'Failed to close session' };
    }
  }

  /**
   * Get store sessions with strict isolation (user can only see their own sessions for the specific store)
   */
  static async getStoreSessionsIsolated(storeId: string, userId: string): Promise<{
    success: boolean;
    data?: any[];
    error?: string;
  }> {
    try {
      const { data, error } = await supabase.rpc('get_store_sessions_isolated', {
        p_store_id: storeId,
        p_user_id: userId
      });

      if (error) {
        console.error('Get isolated store sessions error:', error);
        return { success: false, error: error.message };
      }

      return data;
    } catch (error) {
      console.error('Get isolated store sessions error:', error);
      return { success: false, error: 'Failed to get isolated store sessions' };
    }
  }

  /**
   * Get enhanced store sessions with user info and formatting (DEPRECATED - use getStoreSessionsIsolated)
   */
  static async getStoreSessionsEnhanced(storeId: string): Promise<{
    success: boolean;
    data?: any[];
    error?: string;
  }> {
    try {
      const { data, error } = await supabase.rpc('get_store_sessions_enhanced', {
        p_store_id: storeId
      });

      if (error) {
        console.error('Get enhanced store sessions error:', error);
        return { success: false, error: error.message };
      }

      return data;
    } catch (error) {
      console.error('Get enhanced store sessions error:', error);
      return { success: false, error: 'Failed to get enhanced store sessions' };
    }
  }

  // Get session items with full details using RPC
  static async getSessionItemsWithDetails(sessionId: string): Promise<SessionItemsResponse> {
    try {
      const { data, error } = await supabase.rpc('get_session_items_with_details', {
        p_session_id: sessionId
      });

      if (error) {
        return { success: false, error: error.message };
      }

      if (!data?.success) {
        return { success: false, error: data?.error || 'Failed to get session items' };
      }

      return {
        success: true,
        data: {
          items: data.data || []
        }
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to get session items with details'
      };
    }
  }
}